import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ContactDto, ContactKind } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { Empty, ErrorBanner, Field, Modal, More, Panel } from '../ui';
import { WhatsAppButton, waNumber } from '../whatsapp';
import { ask } from '../dialogs';

const KINDS: Record<ContactKind, string> = { client: 'Cliente', prospect: 'Potenziale', supplier: 'Fornitore', other: 'Altro' };

/** Rubrica: un'anagrafica unica di clienti, potenziali clienti, fornitori e referenti. */
export function ContactsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const isAdmin = ctx.me.user.role === 'admin';
  const [list, setList] = useState<ContactDto[] | null>(null);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'all' | ContactKind>('all');
  const [editing, setEditing] = useState<ContactDto | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await api.get<ContactDto[]>('/api/contacts'));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (list ?? []).filter((c) => (kind === 'all' || c.kind === kind) && (!s || `${c.name} ${c.company ?? ''} ${c.email ?? ''} ${c.phone ?? ''}`.toLowerCase().includes(s)));
  }, [list, q, kind]);

  const toDeal = (c: ContactDto) => ctx.setPage('deals', { newFromContact: { name: c.company || c.name, contactName: c.company ? c.name : '', email: c.email ?? '', phone: c.phone ?? '' } });
  const toQuote = (c: ContactDto) => ctx.setPage('quotes', { newFromDeal: { dealId: null, name: c.company || c.name, email: c.email ?? '', value: 0 } });

  return (
    <div className="panels single">
      <Panel
        title="Rubrica"
        subtitle={list ? `Persone e aziende: clienti, potenziali, fornitori · ${list.length}` : undefined}
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            <input className="deal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca" />
            <select value={kind} title="Quale tipo di contatto vedere" onChange={(e) => setKind(e.target.value as 'all' | ContactKind)}>
              <option value="all">Tutti</option>
              {(Object.keys(KINDS) as ContactKind[]).map((k) => (
                <option key={k} value={k}>
                  {KINDS[k]}
                </option>
              ))}
            </select>
            <button type="button" className="btn dark" title="Aggiungi una persona o un’azienda" onClick={() => setEditing('new')}>
              <Icon name="plus" size={16} /> Nuovo
            </button>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {!list ? (
          <div className="muted small">Caricamento…</div>
        ) : visible.length === 0 ? (
          <Empty icon="users">{list.length === 0 ? 'La rubrica è vuota.' : 'Nessun contatto trovato.'}</Empty>
        ) : (
          <div className="ag-list">
            {visible.map((c) => (
              <div key={c.id} className="ag-row" onClick={() => setEditing(c)}>
                <span className="ag-title">
                  <b>{c.name}</b>
                  {c.company && <span className="muted"> · {c.company}</span>}
                  <div className="muted small">
                    {KINDS[c.kind]}
                    {c.roleTitle ? ` · ${c.roleTitle}` : ''}
                    {c.email ? ` · ${c.email}` : ''}
                    {c.phone ? ` · ${c.phone}` : ''}
                    {isAdmin ? ` · di ${c.ownerName}` : ''}
                  </div>
                </span>
                {c.email && (
                  <a className="btn ghost" href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} title="Scrivi">
                    <Icon name="mail" size={15} />
                  </a>
                )}
                {c.phone && (
                  <a className="btn ghost" href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} title="Chiama">
                    <Icon name="phone" size={15} />
                  </a>
                )}
                {waNumber(c.phone) && <WhatsAppButton ctx={ctx} to={{ name: c.name, phone: c.phone! }} />}
              </div>
            ))}
          </div>
        )}
      </Panel>
      {editing && (
        <ContactModal
          ctx={ctx}
          contact={editing === 'new' ? null : editing}
          onDeal={toDeal}
          onQuote={toQuote}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

export function ContactModal({
  ctx,
  contact,
  prefill,
  onDeal,
  onQuote,
  onClose,
  onSaved,
}: {
  ctx: Ctx;
  contact: ContactDto | null;
  /** Nuova persona già legata a un'azienda (dalla scheda azienda) */
  prefill?: { company?: string };
  onDeal: (c: ContactDto) => void;
  onQuote: (c: ContactDto) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { api } = ctx;
  const [f, setF] = useState({
    name: contact?.name ?? '',
    company: contact?.company ?? prefill?.company ?? '',
    roleTitle: contact?.roleTitle ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    address: contact?.address ?? '',
    vat: contact?.vat ?? '',
    kind: (contact?.kind ?? 'prospect') as ContactKind,
    notes: contact?.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }));

  const save = async () => {
    if (!f.name.trim()) return setError('Indica il nome');
    setBusy(true);
    const body = { ...f, name: f.name.trim(), notes: f.notes.trim() || null, clientId: contact?.clientId ?? null };
    try {
      if (contact) await api.put(`/api/contacts/${contact.id}`, body);
      else await api.post('/api/contacts', body);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!contact || !(await ask(`Eliminare "${contact.name}"?`))) return;
    try {
      await api.del(`/api/contacts/${contact.id}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Modal
      title={contact ? contact.name : 'Nuovo contatto'}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          {contact && (
            <>
              <button type="button" className="btn ghost" onClick={() => void remove()} title="Elimina il contatto">
                <Icon name="trash" size={15} />
              </button>
              <button type="button" className="btn" title="Fissa una visita o una call con questa persona" onClick={() => ctx.setPage('visits', { newVisit: { contactId: contact.id } })}>
                <Icon name="mic" size={15} /> Visita
              </button>
              <button type="button" className="btn" title="Apri una trattativa per seguirla fino al contratto" onClick={() => onDeal(contact)}>
                <Icon name="layers" size={15} /> Trattativa
              </button>
              <button type="button" className="btn" title="Prepara un preventivo" onClick={() => onQuote(contact)}>
                <Icon name="note" size={15} /> Preventivo
              </button>
            </>
          )}
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="fields-2">
        <Field label="Nome e cognome">
          <input value={f.name} onChange={(e) => set({ name: e.target.value })} autoFocus={!contact} />
        </Field>
        <Field label="Società">
          <input value={f.company} onChange={(e) => set({ company: e.target.value })} />
        </Field>
        <Field label="Telefono">
          <input value={f.phone} onChange={(e) => set({ phone: e.target.value })} />
        </Field>
        <Field label="Email">
          <input type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} />
        </Field>
      </div>
      <More open={!!(f.roleTitle || f.address || f.vat || f.notes || f.kind !== 'prospect')}>
        <div className="fields-2">
          <Field label="Tipo">
            <select value={f.kind} onChange={(e) => set({ kind: e.target.value as ContactKind })}>
              {(Object.keys(KINDS) as ContactKind[]).map((k) => (
                <option key={k} value={k}>
                  {KINDS[k]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Ruolo">
            <input value={f.roleTitle} onChange={(e) => set({ roleTitle: e.target.value })} placeholder="Titolare, responsabile IT…" />
          </Field>
          <Field label="Indirizzo" hint="Serve per le visite: arriva già compilato">
            <input value={f.address} onChange={(e) => set({ address: e.target.value })} placeholder="Via…, città" />
          </Field>
          <Field label="P.IVA / Codice fiscale">
            <input value={f.vat} onChange={(e) => set({ vat: e.target.value })} />
          </Field>
        </div>
        <Field label="Note">
          <textarea value={f.notes} onChange={(e) => set({ notes: e.target.value })} rows={3} />
        </Field>
      </More>
    </Modal>
  );
}
